import React, { CSSProperties } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { debounce, throttle } from 'lodash';
import { finder } from '@medv/finder';
import clsx from 'clsx';
import {
  ANY_RID,
  CustomRrwebEventTags,
  EventType,
  eventWithTime,
  Rid,
  RrwebEventMetaMode,
  SbRrwebEvent,
  SbRrwebEvents,
} from 'sb-lib/types';
import { IncrementalSource, ReplayerEvents } from 'sb-lib/rrweb-lib';
import {
  awaitingInitialSnapshot,
  awaitingReplayerFirstRender,
  replayerModeChanged,
} from 'shared/store/dashboard-slice';
import { findSmallestSafeMultiplier } from 'shared/utils/helpers';
import { makeLog } from 'shared/utils/log';
import WidgetRrweb from './WidgetRrweb';
import WidgetVideoInjector from './WidgetVideoInjector';
import { PlayerHint } from './PlayerHint';
import { TakeoverHint } from './PlayerHint/TakeoverHint';
import { InteractionStatus, TakeoverEvent, TakeoverEventType } from 'types';
import { rrwebEventProxyReplace } from 'shared/utils/rrweb-replay-proxy';
import type { AppState } from 'shared/store/reducers';
import { SbReplayer } from 'shared/utils/sb-rrweb';
import {
  sendDrawEventToVisitor,
  sendTakeoverEventToVisitor,
  subscribeToRrweb,
  unsubscribeToRrweb,
} from 'shared/realtime';
import drawCursor from 'shared/assets/images/draw-cursor.svg?inline';
import iframeBackgroundSvg from 'shared/assets/images/rrweb-iframe-background.svg?inline';
import CircularSpinner from '../CircularSpinner';
import { RrwebOptinOverlayInjector } from './RrwebOptinOverlayInjector';
import { SpyOptinOverlay } from './SpyOptinOverlay';
import './style.less';

const log = makeLog('Rrweb');

const INJECTED_VIDEO_IDs = ['__WIDGET_MAIN_VIDEO__', '__WIDGET_SUB_VIDEO__'];
const RRWEB_SCROLL_FREQUENCY = 1100;
const RRWEB_INPUT_COOLDOWN = 500;

type OwnProps = {
  screenTakeoverOn: boolean;
  drawOn: boolean;
  isConnectedToPeer: boolean;
  isReceivingAudio: boolean;
  isReceivingVideo: boolean;
  isTabBackgrounded: boolean;
  isCall: boolean;
  isSpyOnly?: boolean;
  isShowBanner?: boolean;
  isProxy?: boolean;
  isHidden?: boolean;
  onComputeSize?: (width: number, height: number) => void;
  isChatCollapsed: boolean;
  chatMessages: number;
  callStatus: InteractionStatus;
  isHideMouse?: boolean;
};

const mapState = (state: AppState) => ({
  templateSidebar: state.dashboard.templateSidebar,
  isIframeMode: state.dashboard.replayerMode === 'iframe',
  isAwaitingInitialSnapshot: state.dashboard.isAwaitingInitialSnapshot,
});

const connector = connect(mapState, {
  awaitingInitialSnapshot,
  awaitingReplayerFirstRender,
  replayerModeChanged,
});

type Props = ConnectedProps<typeof connector> & OwnProps;

class _Rrweb extends React.Component<Props> {
  replayer = null as null | SbReplayer;
  wrapperRef = React.createRef<HTMLDivElement>();
  playerRef = React.createRef<HTMLDivElement>();
  state = {
    computedAlternativeHeight: null as null | number,
    computedAlternativeWidth: null as null | number,
    isDisableScroll: false,
    incomingAudioOnlyStream: null as null | MediaStream,
    $mainVideo: null as null | HTMLVideoElement,
    $subVideo: null as null | HTMLVideoElement,
    innerStyle: {} as CSSProperties,
    isShowTakeoverHint: false,
    isShowContentEditableHint: false,
    iFrameLoadingRid: '',
  };
  lastIncomingScroll = [-1, -1];
  lastSizeEvent: any = null;
  lastOutgoingInput = 0;
  lastFullsnapshotEventTime = 0;
  lastScrollTriggerEventTime = 0;
  metaRid = null as null | number;
  snapshotRid = null as null | number;

  initReplayer = () => {
    const replayer = new SbReplayer('Main', [], {
      root: this.playerRef.current as Element,
      liveMode: true,
      pauseAnimation: false,
      triggerFocus: false,
      insertStyleRules: [
        // Disallow any interaction with the widget element.
        '.WIDGETROOT > * { cursor: not-allowed !important; }',
        '.WIDGETROOT > * > * { pointer-events: none !important; }',
        // Keep in sync with LIVE_PLAYBACK_HIDE_CLASS in widget/src/app/utils/constants.ts.
        '.servicebell--live-playback-hide { display: none !important; }',
        // Hide video controls
        `#sbstyle video::-webkit-media-controls { display: none; }`,
        // Show draw cursor and snuff unwanted events
        // Note: pointer-events: none cannot be set on same element as
        //       cursor, as it will disable the custom cursor
        `body.__drawOn__ { cursor: url(${drawCursor}) 0 18, crosshair; user-select: none; }`,
        `body.__drawOn__ * { pointer-events: none; }`,
        // Disable textarea resizes
        'textarea { resize: none !important; }',
        // Disable file inputs
        'input[type=file] { pointer-events: none !important; }',
        `iframe { background: no-repeat center #ccc url(${iframeBackgroundSvg})}`,
      ],
    });

    replayer.iframe.removeAttribute('scrolling');
    replayer.iframe.setAttribute('style', 'display: inherit;');
    replayer.iframe.setAttribute('allow', 'autoplay');

    replayer.onSnapshotBuilt(() => this.setIframeHandlers(replayer.iframe));
    replayer.onSnapshotBuilt(this.updateIframeDrawState);
    replayer.onSnapshotBuilt(() => this.props.awaitingReplayerFirstRender(false));

    replayer.on(ReplayerEvents.EventCast, (e?: unknown): void => {
      const event = e as eventWithTime;

      if (
        event?.type === EventType.FullSnapshot ||
        (event?.type === EventType.IncrementalSnapshot &&
          event.data.source === IncrementalSource.Mutation &&
          event.data.adds.length > 0)
      ) {
        this.muteMediaElements(replayer.iframe);
      }
    });

    replayer.sbOn('mode', this.props.replayerModeChanged);
    replayer.sbOn('snapshot', () => {
      if (this.props.isAwaitingInitialSnapshot) {
        log.debug('Initial snapshot received');
        this.props.awaitingInitialSnapshot(false);
        this.handleWindowReposition();
      }
    });

    this.replayer = replayer;
  };

  handleIncomingRrwebEvent: (event: SbRrwebEvents) => void = data => {
    const events = data.events;
    if (!this.replayer) {
      log.error('Got rrweb event but the replayer is not instantiated');
      return;
    }

    const { replayer } = this;
    let rid = data.rid;
    events.forEach(event => {
      // TODO - remove data.synthetic, its here for backwards compatibility for now
      const isSynthetic = event.synthetic || (data as any).synthetic;

      let ignoreEvent = false;
      if (typeof event !== 'string') {
        const source = (event.data as any).source as IncrementalSource;
        rid = isSynthetic ? ANY_RID : rid;

        // scroll
        if (source && source === IncrementalSource.Scroll && !isSynthetic) {
          this.lastIncomingScroll = [(event as any).data.y, Date.now()];
          this.setState({ isDisableScroll: true });
          this.enableScrollDebounced();
        }

        // input
        if (source && source === IncrementalSource.Input) {
          if (Date.now() - this.lastOutgoingInput < RRWEB_INPUT_COOLDOWN) {
            ignoreEvent = true;
            this.setInputDebounced(event as SbRrwebEvent, data.rid, data.mode);
          }
        }

        // iframe loading
        if (
          event.type === EventType.Custom &&
          event.data.tag === CustomRrwebEventTags.IFRAME_LOADING_EVENT_TAG
        ) {
          this.setState({ iFrameLoadingRid: data.rid });
          ignoreEvent = true;
        }
        if (this.state.iFrameLoadingRid && this.state.iFrameLoadingRid !== data.rid) {
          this.setState({ iFrameLoadingRid: '' });
        }
      }

      if (!ignoreEvent) {
        if (typeof event !== 'string' && this.props.isProxy) {
          event = rrwebEventProxyReplace(event, replayer);
        }
        replayer.addEvent(event as eventWithTime, rid, data.mode);
      }
      this.resizeReplayWrapper(event);
    });
  };

  enableScrollDebounced = debounce(() => {
    this.setState({ isDisableScroll: false });
  }, RRWEB_SCROLL_FREQUENCY);

  setInputDebounced = debounce(
    (event: SbRrwebEvent, rid: Rid, mode: RrwebEventMetaMode) => {
      this.replayer?.addEvent(event as eventWithTime, rid, mode);
    },
    RRWEB_INPUT_COOLDOWN,
  );

  handleWindowReposition = () => {
    if (this.lastSizeEvent) {
      this.resizeReplayWrapper(this.lastSizeEvent);
    }
  };

  selectorForTarget = (target: Element, root: Element) => {
    return finder(target, {
      root,
      className: (n: string) => {
        return n !== ':hover';
      },
    });
  };

  selectorForEventWhenTakeover = (event: Event) => {
    const body = this.checkAndGetIframeBody('');
    if (!body || !this.props.screenTakeoverOn) {
      return null;
    }
    if (event.target === body) {
      return null;
    }
    if (event.target) {
      if (event.target === this.replayer?.iframe.contentWindow) {
        return null;
      }
      try {
        const selector = this.selectorForTarget(event.target as Element, body);
        return selector;
      } catch (err) {
        log.error('Could not find selector for', event.target, err as Error);
      }
    }
    return null;
  };

  resizeReplayWrapper = (event: any) => {
    // not all events are relevant - only
    if (event.data.width) {
      this.lastSizeEvent = event;
      const marginH = 16;
      const wrapperMaxWidth =
        (this.wrapperRef.current?.clientWidth || 1000) - marginH * 2;
      const marginV = 0;
      const wrapperMaxHeight =
        (this.wrapperRef.current?.clientHeight || 1000) - marginV * 2;

      const {
        scaleTransform,
        computedAlternativeWidth,
        computedAlternativeHeight,
      } = findSmallestSafeMultiplier(
        event.data.width,
        event.data.height,
        wrapperMaxWidth,
        wrapperMaxHeight,
      );
      this.setState({
        computedAlternativeWidth,
        computedAlternativeHeight,
      });

      if (this.props.onComputeSize) {
        this.props.onComputeSize(computedAlternativeWidth, computedAlternativeHeight);
      }
      const replayerWrapper = document.querySelector(
        '.Rrweb__player .replayer-wrapper',
      ) as HTMLElement;
      if (replayerWrapper) {
        const innerStyle = {
          height: event.data.height + 'px',
          width: event.data.width + 'px',
          transform: `scale(${scaleTransform})`,
          marginTop: (((1 - scaleTransform) * event.data.height) / 2) * -1 + 'px',
          marginLeft: (((1 - scaleTransform) * event.data.width) / 2) * -1 + 'px',
        } as CSSProperties;

        Object.keys(innerStyle).forEach(k => {
          (replayerWrapper.style as any)[k] = innerStyle[k as keyof CSSProperties];
        });

        this.setState({ innerStyle });
      }
    }
  };

  hideShowContentEditableTimer = 0;
  handleContentEditableInteraction = (el: HTMLElement) => {
    if (!this.props.screenTakeoverOn) {
      return;
    }
    const hintId = '__sb_content_editable_hint__';
    if (this.replayer?.iframe.contentDocument) {
      if (this.replayer.iframe.contentDocument.getElementById(hintId)) {
        return;
      }
      const hint = this.replayer.iframe.contentDocument?.createElement('div');
      hint.id = hintId;
      hint.onmouseup = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
      };
      const bb = el.getBoundingClientRect();
      hint.setAttribute(
        'style',
        `
        position: fixed;
        background: rgba(231, 119, 9, 0.23);
        transition: background 1s;
        pointer-events: none;
        left: ${bb.left}px;
        top: ${bb.top}px;
        width: ${bb.width}px;
        height: ${bb.height}px;
      `,
      );
      this.replayer.iframe.contentDocument?.body.append(hint);
      setTimeout(() => (hint.style.background = 'transparent'), 1);
      setTimeout(() => hint.remove(), 1000);
    }
    if (!this.state.isShowContentEditableHint) {
      this.setState({ isShowContentEditableHint: true });
      clearTimeout(this.hideShowContentEditableTimer);
      this.hideShowContentEditableTimer = window.setTimeout(() => {
        this.setState({ isShowContentEditableHint: false });
      }, 3000);
    }
  };

  /*
   * Outgoing takeover event handlers
   */

  handleIframeEvent = (e: Event, extra?: Partial<TakeoverEvent>) => {
    const tagName = (e.target as HTMLElement).tagName;
    const selector = this.selectorForEventWhenTakeover(e);

    const isPreventLocal = e.composedPath().reduce((ignr, x) => {
      const tag = (x as HTMLElement).tagName;
      const href = (x as HTMLLinkElement).href;
      return ignr || (!!tag && tag === 'A' && e.type === 'click' && !!href);
    }, false);

    if (isPreventLocal) {
      log.debug(
        `Preventing local event target ${tagName}/${selector} of type ${e.type} to prevent navigation`,
        { tagName, selector, type: e.type },
      );
      e.preventDefault();
      e.stopPropagation();
    }

    // focus events get played by replayer, we don't want to show hint for that
    if (this.isContentEditable(e) && !['focus', 'focusin', 'focusout'].includes(e.type)) {
      this.handleContentEditableInteraction(e.target as HTMLElement);
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // prevent file input
    if (this.isFileInput(e)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const detail = (e as UIEvent).detail;
    const { clientX, clientY, screenX, screenY } = e as MouseEvent;
    if (selector) {
      const outEvent: TakeoverEvent = {
        selector,
        type: e.type as TakeoverEventType,
        bubbles: e.bubbles,
        cancelable: e.cancelable,
        value: (e.target as any)?.value,
        detail,
        clientX,
        clientY,
        screenX,
        screenY,
        ...extra,
      };
      sendTakeoverEventToVisitor(outEvent);
    }
  };

  isContentEditable = (e: Event): boolean => {
    const isEditable =
      e.target &&
      (e.target as any).getAttribute &&
      (e.target as Element).getAttribute('contenteditable')?.toLowerCase() === 'true';
    if (!isEditable && e.target && (e.target as Node).parentElement) {
      return this.isContentEditable({
        target: (e.target as Node).parentElement,
      } as Event);
    }
    return isEditable;
  };

  isFileInput = (e: Event): boolean => {
    const isFileInput =
      (e.target as HTMLInputElement)?.tagName === 'INPUT' &&
      (e.target as HTMLInputElement)?.type === 'file';
    if (isFileInput) return true;

    const isLabel = (e.target as HTMLLabelElement)?.tagName === 'LABEL';
    const isLabelFor = (e.target as HTMLLabelElement)?.htmlFor;
    if (isLabel && isLabelFor && this.replayer?.iframe.contentDocument) {
      const labeledInput = this.replayer.iframe.contentDocument.getElementById(
        isLabelFor,
      );
      if (
        labeledInput &&
        labeledInput.tagName === 'INPUT' &&
        (labeledInput as HTMLInputElement)?.type === 'file'
      ) {
        return true;
      }
    }

    return false;
  };

  handleIframeInput = (e: Event) => {
    // checkbox input
    const isCheckbox = (e.target as HTMLInputElement)?.type === 'checkbox';
    const checked = isCheckbox ? (e.target as HTMLInputElement)?.checked : undefined;

    return this.handleIframeEvent(e, { checked });
  };

  handleIframeMousemove = throttle((e: MouseEvent) => {
    if (!this.checkAndGetIframeBody('mousemove')) {
      return;
    }
    const mouseDown = e.buttons && (e.buttons & 1) === 1;
    const { clientX, clientY } = e;
    if (this.props.drawOn && mouseDown) {
      const x = e.pageX;
      const y = e.pageY;
      sendDrawEventToVisitor({ type: 'draw', stop: false, x, y });
    } else if (this.props.screenTakeoverOn) {
      sendTakeoverEventToVisitor({ selector: '', clientX, clientY, type: 'mousemove' });
    }
  }, 50);

  handleIframeMouseup = (_: MouseEvent) => {
    if (this.props.drawOn) {
      sendDrawEventToVisitor({ type: 'draw', stop: true, x: 0, y: 0 });
    }
  };

  // must be listen on body with capture=true & passive=false
  suppressIframeScroll = (e: Event) => {
    if (this.props.drawOn && !this.props.screenTakeoverOn) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  };

  handleIframeScroll = throttle((e: Event) => {
    const body = this.checkAndGetIframeBody('scroll');
    if (!body || !this.props.screenTakeoverOn) {
      return;
    }

    if (this.replayer?.iframe.contentWindow) {
      const { target } = e;

      // bail if still in widget scrolled cooldown period
      const [, t] = this.lastIncomingScroll;
      if (Date.now() - t < RRWEB_SCROLL_FREQUENCY) {
        return;
      }

      // bail if widget scroll event is more recent than last admin scroll trigger event
      if (this.lastScrollTriggerEventTime < t) {
        return;
      }

      const { contentDocument } = this.replayer.iframe;
      const selector =
        target === contentDocument
          ? 'document'
          : this.selectorForTarget(target as Element, body);
      const { scrollTop, scrollLeft } = (target && target === contentDocument
        ? contentDocument.scrollingElement || contentDocument.documentElement
        : target) as HTMLElement;

      const data = {
        selector,
        scrollLeft: Math.round(scrollLeft),
        scrollTop: Math.round(scrollTop),
        type: e.type as TakeoverEventType,
      };

      sendTakeoverEventToVisitor(data);
    }
  }, 50);

  handleContenteditableKeypress = (e: Event) => {
    if (this.isContentEditable(e)) {
      this.handleContentEditableInteraction(e.target as HTMLElement);
      e.preventDefault();
      e.stopPropagation();
    }
  };

  handleIframeScrollTriggerEvent = (e: WheelEvent | TouchEvent | KeyboardEvent) => {
    // Wheel event
    if ('deltaX' in e) {
      // no-op, all wheel events are scroll triggers
    }
    // Touchmove event
    else if ('touches' in e) {
      // no-op, all touchmove events are scroll triggers
    }
    // Keyboard event
    else if ('code' in e) {
      const scrollKeyCodes = [
        'Space',
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End',
        'PageUp',
        'PageDown',
      ];
      if (!scrollKeyCodes.includes(e.code)) {
        return;
      }
    }
    this.lastScrollTriggerEventTime = Date.now();
  };

  setIframeHandlers = (iframe: HTMLIFrameElement) => {
    if (this.props.isSpyOnly) {
      return;
    }
    const remAdd = <K extends keyof WindowEventMap>(
      key: K,
      handler: (e: WindowEventMap[K]) => void,
      opts?: boolean | AddEventListenerOptions,
    ) => {
      iframe.contentWindow?.removeEventListener(key, handler);
      iframe.contentWindow?.addEventListener(key, handler, opts);
    };
    remAdd('mousemove', this.handleIframeMousemove);
    remAdd('mouseup', this.handleIframeMouseup);
    // scroll suppression for drawing but not in takeover
    'scroll touchmove mousewheel'.split(' ').forEach(key => {
      iframe.contentDocument?.body.removeEventListener(key, this.suppressIframeScroll);
      iframe.contentDocument?.body.addEventListener(key, this.suppressIframeScroll, {
        capture: true,
        passive: false,
      });
    });
    remAdd('scroll', this.handleIframeScroll, { passive: true, capture: true });
    remAdd('input', this.handleIframeInput);
    remAdd('keypress', this.handleContenteditableKeypress);
    const genericEventTypes = [
      'mouseup',
      'mousedown',
      'click',
      'change',
      'focusin',
      'focus',
      'blur',
      'focusin',
      'focusout',
    ] as const;
    genericEventTypes.forEach(t => remAdd(t, this.handleIframeEvent));
    const scrollTriggerEvents = ['wheel', 'touchmove', 'keyup'] as const;
    scrollTriggerEvents.forEach(t =>
      remAdd(t, this.handleIframeScrollTriggerEvent, { passive: true, capture: true }),
    );
  };

  checkAndGetIframeBody = (label: string) => {
    const body = this.replayer?.getIframeBody() || false;
    if (!body) {
      log.log(`Tried to handle ${label} to send to widget, but missing a req`);
    }
    return body;
  };

  // update draw related data in iframe
  updateIframeDrawState = () => {
    const cd = this.replayer?.iframe.contentDocument;
    if (cd && this.props.drawOn) {
      cd.body.classList.add('__drawOn__');
    } else if (cd) {
      cd.body.classList.remove('__drawOn__');
    }
  };

  muteMediaElements(iframe: HTMLIFrameElement): void {
    const audioElements = iframe.contentDocument?.getElementsByTagName('audio') || [];
    const videoElements = iframe.contentDocument?.getElementsByTagName('video') || [];
    const mediaElements = [...audioElements, ...videoElements];

    for (const element of mediaElements) {
      if (INJECTED_VIDEO_IDs.includes(element.id)) {
        continue;
      }
      element.muted = true;
    }
  }

  componentDidUpdate(prev: Props) {
    if (
      (!prev.screenTakeoverOn && this.props.screenTakeoverOn) ||
      (this.state.isShowTakeoverHint && !prev.isCall && this.props.isCall)
    ) {
      this.setState({ isShowTakeoverHint: false });
    }
    if (prev.isShowBanner !== this.props.isShowBanner) {
      this.handleWindowReposition();
    } else if (prev.templateSidebar !== this.props.templateSidebar) {
      this.handleWindowReposition();
    } else if (prev.isHidden !== this.props.isHidden) {
      this.handleWindowReposition();
    } else if (prev.isChatCollapsed !== this.props.isChatCollapsed) {
      this.handleWindowReposition();
    } else if (prev.chatMessages !== this.props.chatMessages) {
      this.handleWindowReposition();
    } else if (prev.callStatus !== this.props.callStatus) {
      setTimeout(this.handleWindowReposition, this.props.callStatus === 'off' ? 200 : 0);
    }
    if (this.replayer) {
      this.replayer.iframe.setAttribute(
        'tabindex',
        this.props.screenTakeoverOn ? '' : '-1',
      );
      if (prev.drawOn !== this.props.drawOn) {
        this.updateIframeDrawState();
      }
    }
  }

  componentDidMount() {
    this.initReplayer();
    subscribeToRrweb(this.handleIncomingRrwebEvent);
    window.addEventListener('resize', this.handleWindowReposition);
    window.addEventListener('scroll', this.handleWindowReposition);
    this.props.awaitingReplayerFirstRender(true);
  }

  componentWillUnmount() {
    unsubscribeToRrweb(this.handleIncomingRrwebEvent);
    window.removeEventListener('resize', this.handleWindowReposition);
    window.addEventListener('scroll', this.handleWindowReposition);
    if (this.replayer) {
      this.replayer.clearReplayer();
    }
    this.props.awaitingInitialSnapshot(true);
    this.props.awaitingReplayerFirstRender(true);
    clearTimeout(this.hideTakeoverHintTimer);
    clearTimeout(this.hideShowContentEditableTimer);
  }

  hideTakeoverHintTimer = 0;
  handleWrapperClick() {
    if (this.props.isSpyOnly) {
      return;
    }
    if (!this.props.screenTakeoverOn && !this.state.isShowTakeoverHint) {
      this.setState({ isShowTakeoverHint: true });
      clearTimeout(this.hideTakeoverHintTimer);
      this.hideTakeoverHintTimer = window.setTimeout(() => {
        this.setState({ isShowTakeoverHint: false });
      }, 6000);
    } else {
      this.setState({ isShowTakeoverHint: false });
    }
    if (this.props.screenTakeoverOn && this.state.isShowContentEditableHint) {
      this.setState({ isShowContentEditableHint: false });
    }
  }

  render() {
    const { isIframeMode, isTabBackgrounded } = this.props;

    const playerStyle = {
      height: this.state.computedAlternativeHeight || '400px',
      width: this.state.computedAlternativeWidth || '100%',
    };

    const isInteractive = this.props.screenTakeoverOn || this.props.drawOn;
    const playerClass = clsx(
      'Rrweb__player',
      !isInteractive && 'Rrweb__player--uninteractive',
      (isIframeMode || isTabBackgrounded || this.props.isHideMouse) &&
        'Rrweb__player--hide-mouse',
      this.state.iFrameLoadingRid && 'Rrweb__player--backgrounded',
    );

    return (
      <div
        className={clsx(
          'Rrweb',
          this.props.templateSidebar === 'hidden' && 'Rrweb--fullscreen',
          this.props.isHidden && 'Rrweb--hidden',
        )}
        ref={this.wrapperRef}
        onClick={this.handleWrapperClick.bind(this)}
      >
        {(this.state.isDisableScroll || this.state.isShowContentEditableHint) && (
          <div className="Rrweb__event-interceptor" />
        )}
        <TakeoverHint isShow={this.state.isShowTakeoverHint} />
        <PlayerHint isShow={this.state.isShowContentEditableHint}>
          <div style={{ paddingRight: '24px' }}>
            We do not currently support rich text inputs
          </div>
        </PlayerHint>
        <div className={playerClass} style={playerStyle} ref={this.playerRef} />
        <WidgetRrweb
          style={playerStyle}
          innerStyle={this.state.innerStyle}
          showMouse={isIframeMode && !isTabBackgrounded && !this.props.isHideMouse}
        />
        <RrwebOptinOverlayInjector rrwebIframeWrapper={this.wrapperRef} />
        <SpyOptinOverlay />
        {!isIframeMode && (
          <WidgetVideoInjector
            rrwebIframeWrapper={this.wrapperRef}
            isWidgetStream={false}
          />
        )}
        {!!this.state.iFrameLoadingRid && (
          <div className="Rrweb__spinner">
            <CircularSpinner />
          </div>
        )}
      </div>
    );
  }
}

export const Rrweb = connector(_Rrweb);
